// Database utility functions for web version
// Make sure we're using the correct path to the translation.json file
import translationData from '../assets/db/translation.json';

// CRITICAL CHECK - Test if translation data was imported correctly
console.log('=== TRANSLATION DATA IMPORT CHECK ===');
console.log('translationData imported:', translationData ? 'YES' : 'NO');
console.log('Type:', typeof translationData);
console.log('Is empty?', Object.keys(translationData).length === 0 ? 'YES (problem!)' : 'NO');
if (translationData) {
  console.log('Sample entry (1:1):', translationData['1:1']);
}

// Initialize database by loading translation data into localStorage
export const initDatabase = async () => {
  try {
    // Check if translation data is already in localStorage
    const storedData = localStorage.getItem('translationData');
    if (!storedData) {
      console.log('Translation data not found in localStorage, initializing...');
      
      // Count total entries in translation data
      const entryCount = Object.keys(translationData).length;
      console.log(`Translation data contains ${entryCount} entries`);
      
      // Sample a few entries to verify data
      const sampleKeys = Object.keys(translationData).slice(0, 3);
      console.log('Sample entries:', sampleKeys.map(key => ({key, value: translationData[key]})));
      
      // Check if we have Surah 24 data (data mentioned in issue)
      const surah24Entries = Object.keys(translationData).filter(key => key.startsWith('24:'));
      console.log(`Surah 24 has ${surah24Entries.length} entries in data`);
      
      // Store the translation data in localStorage
      localStorage.setItem('translationData', JSON.stringify(translationData));
      console.log('Translation data successfully initialized in localStorage');
    } else {
      console.log('Translation data already exists in localStorage');
    }
    return true;
  } catch (error) {
    console.error('Error initializing database:', error);
    return false;
  }
};

// Get translation data from localStorage
export const getDatabase = async () => {
  try {
    // Try to get data from localStorage first
    const storedData = localStorage.getItem('translationData');
    
    if (storedData) {
      console.log('Retrieved translation data from localStorage');
      return JSON.parse(storedData);
    } else {
      // If not in localStorage, use the imported JSON directly and initialize localStorage
      console.log('No data in localStorage, initializing from imported file');
      await initDatabase();
      return translationData;
    }
  } catch (error) {
    console.error('Error getting database:', error);
    console.log('Falling back to imported translation data');
    // Fallback to using the imported JSON directly
    return translationData;
  }
};

// Fetch teaching tips for a specific surah and verse range
export const fetchTeachingTips = async (surahId, fromAyah, toAyah) => {
  try {
    console.log(`fetchTeachingTips called for Surah ${surahId}, verses ${fromAyah}-${toAyah}`);
    
    const db = await getDatabase();
    console.log(`Database loaded, contains ${Object.keys(db).length} entries`);
    
    // Log a few keys from the database to see their format
    const sampleKeys = Object.keys(db).slice(0, 5);
    console.log("Sample keys in database:", sampleKeys);
    
    const tips = {};
    
    // Check if we have any entries for this surah
    const surahKeys = Object.keys(db).filter(key => key.startsWith(`${surahId}:`));
    console.log(`Found ${surahKeys.length} entries for Surah ${surahId} in database`);
    
    if (surahKeys.length > 0) {
      console.log("First few surah keys:", surahKeys.slice(0, 3));
    }
    
    for (let ayah = fromAyah; ayah <= toAyah; ayah++) {
      // Try different key formats to see which one works
      const keyFormats = [
        `${surahId}:${ayah}`,              // Regular format e.g. "24:1"
        `${surahId.toString().padStart(3, '0')}:${ayah.toString().padStart(3, '0')}`, // Padded format e.g. "024:001"
        `${surahId}_${ayah}`,              // Underscore format e.g. "24_1"
        `s${surahId}a${ayah}`              // s-a format e.g. "s24a1"
      ];
      
      let found = false;
      
      for (const key of keyFormats) {
        if (db[key] && db[key].t) {
          console.log(`Found teaching tip for ${key}: "${db[key].t.substring(0, 50)}..."`);
          tips[`${surahId}:${ayah}`] = db[key]; // Standardize the key format in our return
          found = true;
          break;
        }
      }
      
      if (!found) {
        console.log(`No teaching tip found for ayah ${ayah} using any format`);
        
        // If we're checking the first verse and nothing is found, try to find any key that might be related
        if (ayah === fromAyah) {
          const possibleKeys = Object.keys(db).filter(key => 
            key.includes(surahId.toString()) && 
            key.includes(ayah.toString())
          );
          
          if (possibleKeys.length > 0) {
            console.log(`Found possible related keys: ${possibleKeys.join(", ")}`);
          }
        }
      }
    }
    
    // If no tips were found for the requested range but we have data for the surah, 
    // add the first available verse as a fallback
    if (Object.keys(tips).length === 0 && surahKeys.length > 0) {
      const firstKey = surahKeys[0];
      console.log(`No tips found in range. Adding first available verse (${firstKey}) as fallback`);
      tips[firstKey] = db[firstKey];
    }
    
    console.log(`Found ${Object.keys(tips).length} teaching tips for requested range`);
    return tips;
  } catch (error) {
    console.error('Error fetching teaching tips:', error);
    throw error;
  }
};

export default {
  initDatabase,
  getDatabase,
  fetchTeachingTips,
};